import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import {
  Loader2, CheckCircle2, XCircle, CalendarClock, CreditCard, StickyNote,
  FileText, Phone, MapPin, ClipboardCheck, AlertTriangle, Lock, History,
} from 'lucide-react';
import InvoiceBuilderDialog from '../components/InvoiceBuilderDialog';
import PageHelp from '../components/help/PageHelp';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const cents = (n) => `$${((n || 0) / 100).toFixed(2)}`;
const safeParse = (s) => { try { return parseISO(s); } catch { return null; } };
const todayStr = () => new Date().toISOString().slice(0, 10);

const PAY_BADGE = {
  PAID: 'bg-green-100 text-green-800 border-green-300',
  PARTIAL: 'bg-amber-100 text-amber-800 border-amber-300',
  UNPAID: 'bg-red-100 text-red-700 border-red-300',
};
const STATUS_BADGE = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  CONFIRMED: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-green-100 text-green-800',
  NO_SHOW: 'bg-red-100 text-red-700',
};

export default function CloseoutPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [date, setDate] = useState(todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);

  // Dialogs
  const [noteJob, setNoteJob] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [reschedJob, setReschedJob] = useState(null);
  const [newDateTime, setNewDateTime] = useState('');
  const [partialJob, setPartialJob] = useState(null);
  const [partialDue, setPartialDue] = useState('');
  const [partialPaid, setPartialPaid] = useState('');

  // Close the Day
  const [closeOpen, setCloseOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/dashboard/closeout/history`, { headers });
      setHistory(res.data || []);
    } catch (e) { /* non-blocking */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeTheDay = async () => {
    setClosing(true);
    try {
      await axios.post(`${API_URL}/api/dashboard/closeout/close`, { date }, { headers });
      toast.success('Day closed — recap saved');
      setCloseOpen(false);
      await load();
      await loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not close the day');
    } finally {
      setClosing(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/dashboard/closeout?date=${date}`, { headers });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load closeout');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const patchBooking = async (id, payload, msg) => {
    setActionId(id);
    try {
      await axios.patch(`${API_URL}/api/dashboard/bookings/${id}`, payload, { headers });
      toast.success(msg);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setActionId(null);
    }
  };

  const markPaid = (job) => {
    const due = job.amountDueCents || job.totalCents || 0;
    if (due > 0) {
      patchBooking(job.id, { amountDueCents: due, amountPaidCents: due, paymentStatus: 'PAID' }, 'Marked paid');
    } else {
      patchBooking(job.id, { paymentStatus: 'PAID' }, 'Marked paid');
    }
  };

  const savePartial = async () => {
    if (!partialJob) return;
    const dueCents = Math.round((parseFloat(partialDue) || 0) * 100);
    const paidCents = Math.round((parseFloat(partialPaid) || 0) * 100);
    if (dueCents <= 0) { toast.error('Enter the total amount due'); return; }
    if (paidCents <= 0) { toast.error('Enter how much was paid'); return; }
    if (paidCents >= dueCents) { toast.error('Paid ≥ Due — use "Paid" instead'); return; }
    await patchBooking(
      partialJob.id,
      { amountDueCents: dueCents, amountPaidCents: paidCents, paymentStatus: 'PARTIAL' },
      'Partial payment logged'
    );
    setPartialJob(null); setPartialDue(''); setPartialPaid('');
  };

  const saveNote = async () => {
    if (!noteJob) return;
    const text = noteText.trim();
    if (!text) { setNoteJob(null); setNoteText(''); return; }
    const merged = [(noteJob.providerNotes || '').trim(), text].filter(Boolean).join('\n');
    await patchBooking(noteJob.id, { providerNotes: merged }, 'Note added');
    setNoteJob(null); setNoteText('');
  };

  const saveReschedule = async () => {
    if (!reschedJob || !newDateTime) return;
    setActionId(reschedJob.id);
    try {
      await axios.post(
        `${API_URL}/api/dashboard/bookings/${reschedJob.id}/reschedule`,
        { startDateTime: newDateTime },
        { headers }
      );
      toast.success('Rescheduled');
      setReschedJob(null); setNewDateTime('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reschedule failed');
    } finally {
      setActionId(null);
    }
  };

  const s = data?.summary || {};

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-4" data-testid="closeout-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-1">
            <ClipboardCheck className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold">End-of-Day Closeout</h1>
            <PageHelp
              testId="closeout"
              title="End-of-Day Closeout"
              purpose="Wrap up your day: review what happened, clean up loose ends, and see what still needs payment or an invoice."
              actions={[
                { label: 'Quick actions', desc: 'mark jobs done, paid, or flag for an invoice' },
                { label: 'Close the Day', desc: "save a recap once everything's handled" },
              ]}
              nextStep={'Clear the "Needs Action" list, then tap Close the Day.'}
            />
          </div>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full sm:w-44"
            data-testid="closeout-date-input"
          />
        </div>

        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <>
            {/* Closed-state banner */}
            {data?.closedAt && (
              <div
                className="rounded-md border border-green-300 bg-green-50 px-4 py-3 flex items-center gap-3"
                data-testid="closeout-closed-banner"
              >
                <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                <p className="text-sm text-green-800">
                  Day closed at {format(safeParse(data.closedAt) || new Date(), 'MMM d, h:mm a')}.
                  {' '}You can still make changes and re-close to update the recap.
                </p>
              </div>
            )}

            {/* Money summary */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="closeout-money">
              <MoneyTile label="Estimated Earned" value={cents(s.estimatedTotalEarnedCents)} testid="money-earned" />
              <MoneyTile label="Collected" value={cents(s.amountCollectedCents)} accent="text-green-700" testid="money-collected" />
              <MoneyTile label="Still Owed" value={cents(s.amountOwedCents)} accent="text-red-700" testid="money-owed" />
            </div>

            {/* Count summary */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="closeout-summary">
              <StatTile label="Total Jobs" value={s.totalJobs} testid="stat-total" />
              <StatTile label="Completed" value={s.completed} testid="stat-completed" />
              <StatTile label="No-Shows" value={s.noShows} testid="stat-noshows" />
              <StatTile label="Rescheduled" value={s.rescheduled} testid="stat-rescheduled" />
              <StatTile label="Paid" value={s.paid} testid="stat-paid" />
              <StatTile label="Partial" value={s.partial} testid="stat-partial" />
              <StatTile label="Unpaid" value={s.unpaid} testid="stat-unpaid" />
              <StatTile label="Needs Invoice" value={s.needsInvoice} testid="stat-needs-invoice" />
              <StatTile label="Invoices Sent" value={s.invoicesSentToday} testid="stat-invoices-sent" />
            </div>

            {/* Unfinished jobs */}
            <div className="pt-2">
              <h2 className="text-lg font-semibold flex items-center gap-2 mb-2" data-testid="unfinished-heading">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Needs Action ({data?.unfinishedCount || 0})
              </h2>
              {(!data?.unfinishedJobs || data.unfinishedJobs.length === 0) ? (
                <Card data-testid="closeout-all-clear">
                  <CardContent className="py-10 text-center text-muted-foreground">
                    <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-600" />
                    All wrapped up for this day. Nothing left to close out.
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3" data-testid="unfinished-jobs">
                  {data.unfinishedJobs.map((job) => (
                    <UnfinishedJobCard
                      key={job.id}
                      job={job}
                      loading={actionId === job.id}
                      onMarkDone={() => patchBooking(job.id, { status: 'COMPLETED' }, 'Marked done')}
                      onNoShow={() => patchBooking(job.id, { status: 'NO_SHOW' }, 'Marked no-show')}
                      onMarkPaid={() => markPaid(job)}
                      onMarkUnpaid={() => patchBooking(job.id, { amountPaidCents: 0, paymentStatus: 'UNPAID' }, 'Marked unpaid')}
                      onMarkPartial={() => {
                        setPartialJob(job);
                        setPartialDue(job.amountDueCents != null ? (job.amountDueCents / 100).toFixed(2) : '');
                        setPartialPaid(job.amountPaidCents != null ? (job.amountPaidCents / 100).toFixed(2) : '');
                      }}
                      onNeedsInvoice={() => patchBooking(job.id, { needsInvoice: !job.needsInvoice }, job.needsInvoice ? 'Invoice flag cleared' : 'Flagged: needs invoice')}
                      onReschedule={() => { setReschedJob(job); setNewDateTime(''); }}
                      onAddNote={() => { setNoteJob(job); setNoteText(''); }}
                      onInvoiceSent={load}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Close the Day */}
            <div className="pt-2">
              <Button
                className="w-full h-12 text-base"
                onClick={() => setCloseOpen(true)}
                data-testid="close-the-day-btn"
              >
                <Lock className="h-5 w-5 mr-2" />
                {data?.closedAt ? 'Re-close the Day' : 'Close the Day'}
              </Button>
            </div>

            {/* Recent closeouts */}
            {history.length > 0 && (
              <div className="pt-2">
                <h2 className="text-lg font-semibold flex items-center gap-2 mb-2">
                  <History className="h-5 w-5 text-muted-foreground" />
                  Recent Closeouts
                </h2>
                <div className="space-y-2" data-testid="closeout-history">
                  {history.slice(0, 10).map((h) => (
                    <Card key={h.id} data-testid={`closeout-history-${h.date}`}>
                      <CardContent className="p-3 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium">{h.date}</p>
                          <p className="text-xs text-muted-foreground">
                            {h.summary?.completed ?? 0} done · {h.summary?.noShows ?? 0} no-show
                            {h.unfinishedCountAtClose > 0 ? ` · ${h.unfinishedCountAtClose} left open` : ''}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-semibold tabular-nums text-green-700">{cents(h.summary?.amountCollectedCents)}</p>
                          <p className="text-xs text-muted-foreground">collected</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Close the Day confirmation dialog */}
      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent data-testid="close-day-dialog">
          <DialogHeader>
            <DialogTitle>{data?.closedAt ? 'Re-close the day?' : 'Close the day?'}</DialogTitle>
            <DialogDescription>
              We'll save a recap snapshot for {data?.date} and send you an in-app summary.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <Recap label="Completed" value={s.completed} />
              <Recap label="No-shows" value={s.noShows} />
              <Recap label="Collected" value={cents(s.amountCollectedCents)} />
              <Recap label="Still owed" value={cents(s.amountOwedCents)} />
            </div>
            {(data?.unfinishedCount || 0) > 0 && (
              <div
                className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 flex items-start gap-2"
                data-testid="close-day-warning"
              >
                <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-amber-800 text-xs">
                  {data.unfinishedCount} job(s) still need action. You can close anyway — they'll
                  carry over and show up in your queues.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseOpen(false)}>Cancel</Button>
            <Button onClick={closeTheDay} disabled={closing} data-testid="close-day-confirm">
              {closing && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              <Lock className="h-4 w-4 mr-1" /> {data?.closedAt ? 'Re-close' : 'Close the day'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Note dialog */}
      <Dialog open={!!noteJob} onOpenChange={(o) => !o && setNoteJob(null)}>
        <DialogContent data-testid="closeout-note-dialog">
          <DialogHeader><DialogTitle>Add note</DialogTitle>
            <DialogDescription>Private note — appended to this job's provider notes.</DialogDescription>
          </DialogHeader>
          <Textarea
            rows={4}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Private note for this job…"
            data-testid="closeout-note-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteJob(null)}>Cancel</Button>
            <Button onClick={saveNote} data-testid="closeout-note-save">Save note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule dialog */}
      <Dialog open={!!reschedJob} onOpenChange={(o) => !o && setReschedJob(null)}>
        <DialogContent data-testid="closeout-reschedule-dialog">
          <DialogHeader><DialogTitle>Reschedule job</DialogTitle>
            <DialogDescription>Pick a new date and time. Duration is preserved.</DialogDescription>
          </DialogHeader>
          <Label>New date &amp; time</Label>
          <Input
            type="datetime-local"
            value={newDateTime}
            onChange={(e) => setNewDateTime(e.target.value)}
            data-testid="closeout-reschedule-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReschedJob(null)}>Cancel</Button>
            <Button onClick={saveReschedule} disabled={!newDateTime} data-testid="closeout-reschedule-save">Reschedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Partial payment dialog */}
      <Dialog open={!!partialJob} onOpenChange={(o) => !o && setPartialJob(null)}>
        <DialogContent data-testid="closeout-partial-dialog">
          <DialogHeader><DialogTitle>Partial payment</DialogTitle>
            <DialogDescription>Record how much of the total has been paid so far.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Total due $</Label>
              <Input type="number" step="0.01" min="0" value={partialDue}
                onChange={(e) => setPartialDue(e.target.value)} data-testid="closeout-partial-due" />
            </div>
            <div>
              <Label>Paid so far $</Label>
              <Input type="number" step="0.01" min="0" value={partialPaid}
                onChange={(e) => setPartialPaid(e.target.value)} data-testid="closeout-partial-paid" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPartialJob(null)}>Cancel</Button>
            <Button onClick={savePartial} data-testid="closeout-partial-save">Save partial</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function Recap({ label, value }) {
  return (
    <div className="rounded border p-2">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-semibold tabular-nums">{value ?? 0}</p>
    </div>
  );
}

function MoneyTile({ label, value, accent, testid }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={`text-2xl font-bold mt-1 tabular-nums ${accent || ''}`} data-testid={testid}>{value}</p>
      </CardContent>
    </Card>
  );
}

function StatTile({ label, value, testid }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-2xl font-bold mt-1 tabular-nums" data-testid={testid}>{value ?? 0}</p>
      </CardContent>
    </Card>
  );
}

function UnfinishedJobCard({
  job, loading, onMarkDone, onNoShow, onMarkPaid, onMarkUnpaid, onMarkPartial,
  onNeedsInvoice, onReschedule, onAddNote, onInvoiceSent,
}) {
  const pay = job.paymentStatus || 'UNPAID';
  const when = job.startDateTime ? format(safeParse(job.startDateTime) || new Date(), 'h:mm a') : '';
  const dim = job.status === 'COMPLETED' || job.status === 'NO_SHOW';
  return (
    <Card className={dim ? 'opacity-70' : ''} data-testid={`closeout-job-${job.id}`}>
      <CardContent className="p-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-semibold truncate">{job.clientName || 'Client'}</p>
            <p className="text-xs text-muted-foreground">{job.serviceName || 'Service'}{when ? ` · ${when}` : ''}</p>
            {job.barnName && <p className="text-xs flex items-center gap-1"><MapPin className="h-3 w-3" />{job.barnName}</p>}
          </div>
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            <Badge className={STATUS_BADGE[job.status] || ''}>{job.status}</Badge>
            <Badge variant="outline" className={PAY_BADGE[pay]}>{pay}</Badge>
            {job.needsInvoice && (
              <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300" data-testid={`closeout-needs-invoice-badge-${job.id}`}>Needs invoice</Badge>
            )}
          </div>
        </div>

        {job.clientPhone && (
          <a href={`tel:${job.clientPhone}`} className="text-xs text-primary flex items-center gap-1">
            <Phone className="h-3 w-3" />{job.clientPhone}
          </a>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1">
          <Button size="sm" variant="outline" className="h-9" disabled={loading}
            onClick={onMarkDone} data-testid={`closeout-mark-done-${job.id}`}>
            <CheckCircle2 className="h-4 w-4 mr-1" /> Done
          </Button>
          <Button size="sm" variant="outline" className="h-9" disabled={loading}
            onClick={onNoShow} data-testid={`closeout-no-show-${job.id}`}>
            <XCircle className="h-4 w-4 mr-1" /> No Show
          </Button>
          <Button size="sm" variant={pay === 'PAID' ? 'default' : 'outline'} className="h-9" disabled={loading}
            onClick={onMarkPaid} data-testid={`closeout-pay-paid-${job.id}`}>
            <CreditCard className="h-4 w-4 mr-1" /> Paid
          </Button>
          <Button size="sm" variant={pay === 'PARTIAL' ? 'default' : 'outline'} className="h-9" disabled={loading}
            onClick={onMarkPartial} data-testid={`closeout-pay-partial-${job.id}`}>
            Partial
          </Button>
          <Button size="sm" variant={pay === 'UNPAID' ? 'default' : 'outline'} className="h-9" disabled={loading}
            onClick={onMarkUnpaid} data-testid={`closeout-pay-unpaid-${job.id}`}>
            Unpaid
          </Button>
          <Button size="sm" variant="outline" className="h-9" disabled={loading}
            onClick={onReschedule} data-testid={`closeout-reschedule-${job.id}`}>
            <CalendarClock className="h-4 w-4 mr-1" /> Reschedule
          </Button>
          <Button size="sm"
            variant={job.needsInvoice ? 'default' : 'outline'}
            className={`h-9 ${job.needsInvoice ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}`}
            disabled={loading}
            onClick={onNeedsInvoice} data-testid={`closeout-needs-invoice-${job.id}`}>
            <FileText className="h-4 w-4 mr-1" /> {job.needsInvoice ? 'Invoiced?' : 'Needs Invoice'}
          </Button>
          <InvoiceBuilderDialog
            booking={job}
            onSent={onInvoiceSent}
            trigger={
              <Button size="sm" variant="outline" className="h-9" data-testid={`closeout-create-invoice-${job.id}`}>
                <FileText className="h-4 w-4 mr-1" /> Create Invoice
              </Button>
            }
          />
          <Button size="sm" variant="outline" className="h-9" disabled={loading}
            onClick={onAddNote} data-testid={`closeout-add-note-${job.id}`}>
            <StickyNote className="h-4 w-4 mr-1" /> Add Note
          </Button>
        </div>

        {job.providerNotes && (
          <p className="text-xs text-muted-foreground border-l-2 pl-2 line-clamp-2">{job.providerNotes}</p>
        )}
      </CardContent>
    </Card>
  );
}
